import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import EventTimeline from '../../components/EventTimeline';
import './CreateEvent.css';

const CreateEvent: React.FC = () => {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [timeline, setTimeline] = useState<any>(null);
  const [organizerSettings, setOrganizerSettings] = useState<any>(null);
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [priceAccepted, setPriceAccepted] = useState(false);

  // Redirect non-organizers to become organizer page
  useEffect(() => {
    if (user && user.role !== 'organizer' && user.role !== 'admin') {
      navigate('/bli-arrangor');
    }
  }, [user, navigate]);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '16:00',
    location: '',
    address: '',
    maxParticipants: 24,
    maxDistanceKm: 5,
    description: '',
    organizerName: '',
    organizerEmail: '',
    organizerPhone: '',
    appetizer: true,
    mainCourse: true,
    dessert: true,
    appetizerDuration: 90,
    mainCourseDuration: 120,
    dessertDuration: 90,
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Fetch system settings on mount
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/settings`);
        const settings = response.data;
        setSystemSettings(settings);
        
        // Apply system defaults to form
        if (settings.eventDefaults) {
          setFormData(prev => ({
            ...prev,
            appetizerDuration: settings.eventDefaults.appetizerDuration || 90,
            mainCourseDuration: settings.eventDefaults.mainCourseDuration || 120,
            dessertDuration: settings.eventDefaults.dessertDuration || 90,
            maxParticipants: settings.eventDefaults.defaultParticipants || 24,
            maxDistanceKm: settings.eventDefaults.defaultRadiusKm || 5,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch system settings:', err);
      }
    };

    fetchSystemSettings();
  }, []);

  // Fetch organizer settings on mount
  useEffect(() => {
    const fetchOrganizerSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/organizers/settings`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const settings = response.data.settings;
        setOrganizerSettings(settings);
        
        // Prefill form with organizer data
        setFormData(prev => ({
          ...prev,
          location: settings.invoiceAddress?.city || '',
          address: settings.invoiceAddress ? `${settings.invoiceAddress.street}, ${settings.invoiceAddress.city}` : '',
          organizerName: settings.organizerName || '',
          organizerEmail: settings.email || '',
          organizerPhone: settings.phone || '',
        }));
      } catch (err) {
        console.error('Failed to fetch organizer settings:', err);
      }
    };

    if (accessToken) {
      fetchOrganizerSettings();
    }
  }, [accessToken]);

  // Calculate timeline when date, time, or durations change
  useEffect(() => {
    if (formData.date && formData.time) {
      calculateTimeline();
    }
  }, [formData.date, formData.time, formData.appetizerDuration, formData.mainCourseDuration, formData.dessertDuration]);

  const calculateTimeline = () => {
    const eventDateTime = `${formData.date}T${formData.time}:00`;
    const eventDate = new Date(eventDateTime);

    // Use custom durations from form
    const appetizerDuration = formData.appetizerDuration;
    const mainCourseDuration = formData.mainCourseDuration;
    const dessertDuration = formData.dessertDuration;

    const formatNorwegianDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    };

    const formatNorwegianTime = (date: Date) => {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    // Pre-event dates
    const invitationCloseDate = new Date(eventDate);
    invitationCloseDate.setDate(invitationCloseDate.getDate() - 5);
    invitationCloseDate.setHours(23, 59, 0, 0);

    const mealAssignmentDate = new Date(eventDate);
    mealAssignmentDate.setDate(mealAssignmentDate.getDate() - 4);
    mealAssignmentDate.setHours(10, 0, 0, 0);

    // Event day timeline
    // Starttidspunkt is the fixed start time (when appetizer is served)
    const starterTime = new Date(eventDate.getTime());
    
    // First message: 30 minutes before start
    const firstMessageTime = new Date(eventDate.getTime() - 30 * 60000);
    
    // Hovedrett starts at: Start time + Forrett duration
    const mainCourseTime = new Date(eventDate.getTime() + appetizerDuration * 60000);
    
    // Message about hovedrett: in the middle between forrett and hovedrett
    // Transit time = appetizerDuration, so middle = appetizerDuration / 2
    const mainCourseMessageTime = new Date(eventDate.getTime() + (appetizerDuration / 2) * 60000);
    
    // Dessert starts at: Hovedrett time + Hovedrett duration
    const dessertTime = new Date(mainCourseTime.getTime() + mainCourseDuration * 60000);
    
    // Message about dessert: in the middle between hovedrett and dessert
    const dessertMessageTime = new Date(mainCourseTime.getTime() + (mainCourseDuration / 2) * 60000);
    
    // Fest starts at: Dessert time + Dessert duration
    const finalPartyTime = new Date(dessertTime.getTime() + dessertDuration * 60000);

    const timelineData = {
      eventDate: formatNorwegianDate(eventDate),
      eventTime: formatNorwegianTime(eventDate),
      invitationCloseDate: formatNorwegianDate(invitationCloseDate),
      invitationCloseTime: formatNorwegianTime(invitationCloseDate),
      mealAssignmentDate: formatNorwegianDate(mealAssignmentDate),
      mealAssignmentTime: formatNorwegianTime(mealAssignmentDate),
      firstMessageTime: formatNorwegianTime(firstMessageTime),
      starterTime: formatNorwegianTime(starterTime),
      mainCourseMessageTime: formatNorwegianTime(mainCourseMessageTime),
      mainCourseTime: formatNorwegianTime(mainCourseTime),
      dessertMessageTime: formatNorwegianTime(dessertMessageTime),
      dessertTime: formatNorwegianTime(dessertTime),
      finalPartyTime: formatNorwegianTime(finalPartyTime),
    };
    console.log('Timeline calculated:', timelineData);
    setTimeline(timelineData);
  };

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setIsSearchingAddress(true);
    try {
      // Using Geonorge API (Norwegian official address database)
      const response = await fetch(
        `https://ws.geonorge.no/adresser/v1/sok?sok=${encodeURIComponent(query)}&treffPerSide=10`
      );
      const data = await response.json();
      
      if (data.adresser && data.adresser.length > 0) {
        const suggestions = data.adresser.map((addr: any) => ({
          address: addr.adressetekst,
          municipality: addr.poststed || addr.kommunenavn,
          position: null
        }));
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (err) {
      console.error('Address search error:', err);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, address: value });
    searchAddress(value);
  };

  const selectAddress = (suggestion: any) => {
    const fullAddress = suggestion.municipality 
      ? `${suggestion.address}, ${suggestion.municipality}`
      : suggestion.address;
    setFormData({ ...formData, address: fullAddress });
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  // Get valid participant counts based on number of courses
  const getValidParticipantCounts = () => {
    const courseCount = [formData.appetizer, formData.mainCourse, formData.dessert].filter(Boolean).length;
    if (courseCount === 0) return [];
    
    const divisor = courseCount * 2; // courses × 2 people per pair
    const counts = [];
    for (let i = divisor; i <= 300; i += divisor) {
      counts.push(i);
    }
    return counts;
  };

  const handleParticipantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setFormData({ ...formData, maxParticipants: value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.date || !formData.location || !formData.address) {
      setError('Vennligst fyll ut alle påkrevde felt');
      return;
    }

    if (!formData.organizerName || !formData.organizerEmail || !formData.organizerPhone) {
      setError('Arrangørkontaktinformasjon er påkrevd');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.organizerEmail)) {
      setError('Vennligst oppgi en gyldig e-postadresse');
      return;
    }

    if (formData.maxParticipants < 4 || formData.maxParticipants > 300) {
      setError('Antall deltakere må være mellom 4 og 300');
      return;
    }

    if (!formData.appetizer && !formData.mainCourse && !formData.dessert) {
      setError('Du må velge minst én rett');
      return;
    }

    // Count number of courses
    const numberOfCourses = [formData.appetizer, formData.mainCourse, formData.dessert].filter(Boolean).length;
    
    // Calculate minimum pairs: 3 pairs per course to ensure no repeat meetings
    const minPairs = numberOfCourses * 3;
    const maxPairs = Math.floor(formData.maxParticipants / 2);
    
    if (maxPairs < minPairs) {
      setError(`Med ${numberOfCourses} retter må du ha minst ${minPairs} par (${minPairs * 2} deltakere) for at alle skal møte nye par på hver rett.`);
      return;
    }
    
    // Participants must be divisible by number of courses AND by 2 (pairs)
    const divisor = numberOfCourses * 2;
    if (formData.maxParticipants % divisor !== 0) {
      setError(`Antall deltakere må kunne deles på ${divisor} (${numberOfCourses} retter × 2 personer per par)`);
      return;
    }

    setLoading(true);

    try {
      // User enters time in Norway local time (Europe/Oslo)
      // We need to send this to the backend as UTC
      // Note: The date/time input is in local browser time
      // For Norway-based users, this should match Europe/Oslo
      const eventDateTime = `${formData.date}T${formData.time}:00`;
      const localDate = new Date(eventDateTime);
      // Convert to ISO string (UTC) for storage
      const utcDateTime = localDate.toISOString();

      const response = await axios.post(
        `${API_URL}/organizers/events`,
        {
          name: formData.name,
          date: utcDateTime,
          location: formData.location,
          address: formData.address,
          maxParticipants: formData.maxParticipants,
          maxDistanceKm: formData.maxDistanceKm,
          description: formData.description,
          organizerName: formData.organizerName,
          organizerEmail: formData.organizerEmail,
          organizerPhone: formData.organizerPhone,
          courses: {
            appetizer: formData.appetizer,
            mainCourse: formData.mainCourse,
            dessert: formData.dessert,
          },
          appetizerDuration: formData.appetizerDuration,
          mainCourseDuration: formData.mainCourseDuration,
          dessertDuration: formData.dessertDuration,
          pricePerEvent: organizerSettings?.pricePerEvent,
          pricePerEventAccepted: priceAccepted,
          netDays: organizerSettings?.netDays,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      navigate(`/arrangor/arrangement/${response.data.id}`);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Kunne ikke opprette arrangement. Prøv igjen.'
      );
    } finally {
      setLoading(false);
    }
  };

  const courseCount = [formData.appetizer, formData.mainCourse, formData.dessert].filter(Boolean).length;

  return (
    <div className="create-event-page">
      <div className="create-event-container">
        <div className="page-header">
          <h1>Opprett nytt Sykkelfest-arrangement</h1>
          <p className="subtitle">
            Fyll ut informasjon om arrangementet. Deltakere vil kunne registrere seg i par.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="event-form">
          <div className="form-section">
            <h2>Grunnleggende informasjon</h2>
            
            <div className="form-group">
              <label htmlFor="name" className="form-label required">
                Navn på arrangement
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                placeholder="Sykkelfest i Bergen sentrum"
                disabled={loading}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date" className="form-label required">
                  Dato for sykkelfest
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  className="form-input"
                  value={formData.date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  disabled={loading}
                  required
                />
                <small className="form-hint">
                  Velg dato fra kalenderen
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="time" className="form-label required">
                  Starttidspunkt
                </label>
                <input
                  type="time"
                  id="time"
                  name="time"
                  className="form-input"
                  value={formData.time}
                  onChange={handleChange}
                  disabled={loading}
                  required
                />
                <small className="form-hint">
                  Velg tidspunkt
                </small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="location" className="form-label required">
                Sted/område
              </label>
              <input
                type="text"
                id="location"
                name="location"
                className="form-input"
                value={formData.location}
                onChange={handleChange}
                placeholder="Bergen sentrum"
                disabled={loading}
                required
              />
              <small className="form-hint">
                Generelt område hvor arrangementet finner sted
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="address" className="form-label required">
                Sluttdestinasjon
              </label>
              <div className="address-search-wrapper">
                <input
                  type="text"
                  id="address"
                  name="address"
                  className="form-input"
                  value={formData.address}
                  onChange={handleAddressChange}
                  onFocus={() => formData.address.length >= 3 && addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                  placeholder="F.eks. Mathallen Oslo, Vulkan 5, 0178 Oslo"
                  disabled={loading}
                  required
                  autoComplete="off"
                />
                {isSearchingAddress && (
                  <div className="address-search-spinner">Søker...</div>
                )}
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div className="address-suggestions">
                    {addressSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="address-suggestion-item"
                        onClick={() => selectAddress(suggestion)}
                      >
                        <div className="suggestion-main">
                          {suggestion.address}
                          {suggestion.municipality && (
                            <span className="suggestion-municipality"> • {suggestion.municipality}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <small className="form-hint">
                Felles møtested etter alle rettene. Søk etter adresse i Norge (minst 3 tegn).
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="maxDistanceKm" className="form-label required">
                Maksimal avstand fra sluttdestinasjon
              </label>
              <div className="slider-container">
                <div className="slider-value-bubble">
                  <strong>{formData.maxDistanceKm}</strong> km
                </div>
                <input
                  type="range"
                  id="maxDistanceKm"
                  name="maxDistanceKm"
                  className="form-slider"
                  value={formData.maxDistanceKm}
                  onChange={(e) => setFormData({ ...formData, maxDistanceKm: Number(e.target.value) })}
                  min={1}
                  max={20}
                  step={0.5}
                  disabled={loading}
                  required
                />
                <div className="slider-track-fill" style={{
                  width: `${((formData.maxDistanceKm - 1) / (20 - 1)) * 100}%`
                }}></div>
              </div>
              <div className="slider-labels">
                <span>1 km</span>
                <span>10 km</span>
                <span>20 km</span>
              </div>
              <small className="form-hint">
                Deltakere som bor utenfor denne radiusen (luftlinje) fra sluttstedet vil ikke kunne melde seg på. 
                Dette sikrer overkommelige sykkelavstander og god ruteplanlegging.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Beskrivelse (valgfritt)
              </label>
              <textarea
                id="description"
                name="description"
                className="form-textarea"
                value={formData.description}
                onChange={handleChange}
                placeholder="Ekstra informasjon om arrangementet... til glede og inspirasjon for deltakerne..."
                rows={4}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Arrangørkontakt</h2>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              Denne informasjonen vises til deltakerne slik at de kan kontakte deg ved behov.
            </p>
            
            <div className="form-group">
              <label htmlFor="organizerName" className="form-label required">
                Navn på arrangør
              </label>
              <input
                type="text"
                id="organizerName"
                name="organizerName"
                className="form-input"
                value={formData.organizerName}
                onChange={handleChange}
                placeholder="Kari Nordmann"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="organizerEmail" className="form-label required">
                E-post
              </label>
              <input
                type="email"
                id="organizerEmail"
                name="organizerEmail"
                className="form-input"
                value={formData.organizerEmail}
                onChange={handleChange}
                placeholder="kari@example.com"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="organizerPhone" className="form-label required">
                Telefonnummer
              </label>
              <input
                type="tel"
                id="organizerPhone"
                name="organizerPhone"
                className="form-input"
                value={formData.organizerPhone}
                onChange={handleChange}
                placeholder="+47 123 45 678"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Retter og deltakere</h2>

            <div className="form-group">
              <label className="form-label required">Hvilke retter inkluderes, og hvor lang tid per rett?</label>
              <div className="course-selection-group">
                <div className="course-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="appetizer"
                      checked={formData.appetizer}
                      onChange={handleChange}
                      disabled={true}
                    />
                    <span>Forrett</span>
                  </label>
                  <div className="duration-input-wrapper">
                    <input
                      type="number"
                      name="appetizerDuration"
                      className="form-input duration-input"
                      value={formData.appetizerDuration}
                      onChange={handleChange}
                      min="15"
                      max="180"
                      step="5"
                      disabled={loading || !formData.appetizer}
                    />
                    <span className="duration-label">min</span>
                  </div>
                </div>
                <div className="course-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="mainCourse"
                      checked={formData.mainCourse}
                      onChange={handleChange}
                      disabled={true}
                    />
                    <span>Hovedrett</span>
                  </label>
                  <div className="duration-input-wrapper">
                    <input
                      type="number"
                      name="mainCourseDuration"
                      className="form-input duration-input"
                      value={formData.mainCourseDuration}
                      onChange={handleChange}
                      min="15"
                      max="180"
                      step="5"
                      disabled={loading || !formData.mainCourse}
                    />
                    <span className="duration-label">min</span>
                  </div>
                </div>
                <div className="course-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="dessert"
                      checked={formData.dessert}
                      onChange={handleChange}
                      disabled={true}
                    />
                    <span>Dessert</span>
                  </label>
                  <div className="duration-input-wrapper">
                    <input
                      type="number"
                      name="dessertDuration"
                      className="form-input duration-input"
                      value={formData.dessertDuration}
                      onChange={handleChange}
                      min="15"
                      max="180"
                      step="5"
                      disabled={loading || !formData.dessert}
                    />
                    <span className="duration-label">min</span>
                  </div>
                </div>
              </div>
              {courseCount > 0 && (
                <small className="form-hint">
                  {courseCount} rett{courseCount > 1 ? 'er' : ''} valgt
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="maxParticipants" className="form-label required">
                Maksimalt antall deltakere
              </label>
              <div className="slider-container">
                <div className="slider-value-bubble">
                  <strong>{formData.maxParticipants}</strong> deltakere
                </div>
                <input
                  type="range"
                  id="maxParticipants"
                  name="maxParticipants"
                  className="form-slider"
                  value={formData.maxParticipants}
                  onChange={handleParticipantChange}
                  min={courseCount > 0 ? courseCount * 6 : 6}
                  max={300}
                  step={courseCount > 0 ? courseCount * 2 : 6}
                  disabled={loading}
                  list="participant-marks"
                  required
                />
                <div className="slider-track-fill" style={{
                  width: `${((formData.maxParticipants - (courseCount > 0 ? courseCount * 6 : 6)) / (300 - (courseCount > 0 ? courseCount * 6 : 6))) * 100}%`
                }}></div>
              </div>
              <div className="slider-labels">
                <span>{courseCount > 0 ? courseCount * 6 : 6}</span>
                <span>150</span>
                <span>300</span>
              </div>
              <small className="form-hint">
                Minimum: {courseCount * 3} par ({courseCount * 6} deltakere) - 
                slik at alle møter nye par på hver rett. 
                Verdier må kunne deles på {courseCount > 0 ? courseCount * 2 : 6}.
              </small>
            </div>
          </div>

          {timeline && formData.date ? (
            <div className="form-section">
              <h2>Forhåndsvisning av tidsplan</h2>
              <p className="timeline-preview-intro">
                Slik vil tidspunktet for arrangementet se ut for deltakerne:
              </p>
              <EventTimeline eventName={formData.name} eventDate={`${formData.date}T${formData.time}:00`} timeline={timeline} />
            </div>
          ) : (
            console.log('Timeline not showing - timeline:', timeline, 'formData.date:', formData.date),
            null
          )}

          {/* Price Acceptance Section */}
          {organizerSettings?.pricePerEvent && (
            <div className="form-section price-section">
              <h2>Pris og betaling</h2>
              <div className="price-info-box">
                <div className="price-amount">
                  <span className="price-label">Pris for dette arrangementet:</span>
                  <span className="price-value">{organizerSettings.pricePerEvent.toLocaleString('nb-NO')} kr</span>
                </div>
                <p className="price-description">
                  Dette beløpet vil bli fakturert i henhold til gjeldende betalingsbetingelser, netto {organizerSettings.netDays || 14} dager.
                  Deltakerne betaler ingenting for sin deltagelse - kun arrangør betaler for å bruke Sykkelfest plattformen.
                </p>
                <div className="price-acceptance">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={priceAccepted}
                      onChange={(e) => setPriceAccepted(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Jeg godtar at dette beløpet vil bli fakturert</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/arrangor/dashboard')}
              disabled={loading}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || (organizerSettings?.pricePerEvent && !priceAccepted)}
            >
              {loading && (
                <span className="spinner" role="status" aria-label="Laster">
                  <svg className="spinner-icon" viewBox="0 0 50 50">
                    <circle
                      className="spinner-path"
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      strokeWidth="4"
                    ></circle>
                  </svg>
                </span>
              )}
              {loading ? 'Oppretter...' : 'Opprett arrangement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEvent;
